
import React, { useState } from 'react';
import { 
  Table, 
  TableBody, 
  TableHead, 
  TableHeader, 
  TableRow 
} from '@/components/ui/table';
import { ExamResult } from '@/services/db/types';
import DeleteConfirmDialog from './DeleteConfirmDialog';
import { useNavigate } from 'react-router-dom';
import { printStudentExamReport, printSpecificExamCertificate } from '@/utils/printing/exam';
import ExamEmptyState from './ExamEmptyState';
import ExamTableHeader from './ExamTableHeader';
import ExamTableRow from './ExamTableRow';
import { toast } from 'sonner';
import { getBrowserInfo } from '@/utils/printing/printingBridge';

interface ExamResultsTableProps {
  examResults: ExamResult[];
  loading: boolean;
  onDelete: (examId: string) => Promise<boolean>;
  studentId: number | string;
}

const ExamResultsTable = ({ 
  examResults, 
  loading, 
  onDelete,
  studentId 
}: ExamResultsTableProps) => {
  const [deletingExamId, setDeletingExamId] = useState<string | null>(null);
  const [examToDelete, setExamToDelete] = useState<ExamResult | null>(null);
  const navigate = useNavigate();
  
  const handleEdit = (exam: ExamResult) => {
    navigate(`/students/${studentId}/exams/${exam.id}`);
  };

  const handleDelete = (exam: ExamResult) => {
    setDeletingExamId(exam.id);
    setExamToDelete(exam);
  };

  // Print all exams as a summary report using standardized print system
  const handlePrintExams = async () => {
    if (examResults.length === 0) {
      toast.error("No exam results to print");
      return;
    }
    
    try {
      // Log environment info for debugging
      const browserInfo = getBrowserInfo();
      console.log('Print environment for exam report:', browserInfo);
      
      // Special handling for desktop environments
      if (browserInfo.isDesktopApp) {
        toast.info("Opening exam report in desktop compatible format...");
      }
      
      toast.info("Generating exam report with standardized layout...");
      await printStudentExamReport(studentId, examResults);
    } catch (error) {
      console.error('Error printing exam report:', error);
      toast.error("Failed to generate exam report");
    }
  };
  
  // Print a specific exam certificate using standardized print system
  const handlePrintCertificate = async (examId: string) => {
    try {
      const exam = examResults.find(exam => exam.id === examId);
      if (!exam) {
        toast.error("Exam not found");
        return;
      }
      
      // Log environment info for debugging
      const browserInfo = getBrowserInfo();
      console.log('Print environment for certificate:', browserInfo);
      
      // Special handling for desktop environments
      if (browserInfo.isDesktopApp) {
        toast.info("Opening certificate in desktop compatible format...");
      }
      
      toast.info("Generating exam certificate with standardized layout...");
      await printSpecificExamCertificate(studentId, examId);
    } catch (error) {
      console.error('Error printing exam certificate:', error);
      toast.error("Failed to generate exam certificate");
    }
  };

  if (loading) {
    return (
      <div className="rounded-md border p-6 text-center">
        <p className="text-muted-foreground">Loading exams...</p>
      </div>
    );
  }

  if (examResults.length === 0) {
    return <ExamEmptyState />;
  }

  return (
    <>
      <div className="rounded-md border">
        <ExamTableHeader onPrintExam={handlePrintExams} />
        <div className="overflow-x-auto">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Subject</TableHead>
                <TableHead>Exam Type</TableHead>
                <TableHead>Date</TableHead>
                <TableHead>Score</TableHead>
                <TableHead>Program</TableHead>
                <TableHead className="text-right">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {examResults.map((exam) => (
                <ExamTableRow 
                  key={exam.id}
                  exam={exam}
                  onDelete={() => handleDelete(exam)}
                  studentId={studentId}
                  onPrintCertificate={() => handlePrintCertificate(exam.id)}
                />
              ))}
            </TableBody>
          </Table>
        </div>
      </div>

      {/* Delete Confirm Dialog */}
      {deletingExamId && examToDelete && (
        <DeleteConfirmDialog
          isOpen={!!deletingExamId}
          onClose={() => setDeletingExamId(null)}
          onDelete={async () => {
            if (deletingExamId) {
              const success = await onDelete(deletingExamId);
              if (success) {
                setDeletingExamId(null);
              }
              return success;
            }
            return false;
          }}
          examResult={examToDelete}
        />
      )}
    </>
  );
};

export default ExamResultsTable;
